import Link from "common/Typography/Link";
import React, { useContext, useEffect } from "react";
import { displayResultMessage } from "utils/misc";
import { localStorageService } from "utils/localStorageService";
import TelegramUserPreview from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/TelegramUserPreview";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { GetBasicDetailsFromUuidData } from "@shared/features/onboarding/types";
import { OnboardingStep } from "analytics/events";
import { useIsOnboarding } from "utils/hooks/misc";
import useValidateConnectTelegram from "@shared/features/onboarding/hooks/useValidateConnectTelegram";
import { LanguageNameSpaces, Platform } from "@shared/types";
import Modal from "common/Modal/Modal";
import { Security } from "common/Icons/Icons";
import VerificationCode from "features/auth/VerificationCode";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { PrimaryTitle, Wrapper } from "features/auth/styles";
import SendCodeButton from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/SendCodeButton";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface ValidateConnectTelegramProps {
  uuid?: string;
  setUuid?: (uuid: string) => void;
  getBasicDetailsFromUuidData?: GetBasicDetailsFromUuidData;
  onSuccess?: () => void;
  onCancel?: () => void;
  code?: string;
  inModal?: boolean;
}

const ValidateConnectTelegram = ({
  uuid,
  setUuid,
  getBasicDetailsFromUuidData,
  onSuccess,
  onCancel,
  code,
  inModal,
}: ValidateConnectTelegramProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  const {
    onSendAuthCodeClick,
    continueButtonData,
    securityCodeInputData,
    handleRemoveUuids,
    storageData,
    defaultTitle,
    openRemoveUuidConfirmationModal,
    removeUuidModalData,
  } = useValidateConnectTelegram({
    displayResultMessage,
    code,
    getBasicDetailsFromUuidData,
    shortUuid: uuid,
    setUuid,
    onSuccess: () => {
      localStorageService.removeUuid();
      onSuccess?.();
    },
    onCancel,
    platform: Platform.WEBSITE,
  });
  const isOnboarding = useIsOnboarding();

  useEffect(() => {
    analytics?.validateTelegramView({
      onboardingStep: isOnboarding ? OnboardingStep.METHOD_SETTINGS : undefined,
    });
  }, [analytics]);

  const confirmRemoveUuidModal = (
    <Modal
      title={removeUuidModalData.title}
      open={removeUuidModalData.isVisible}
      onCancel={removeUuidModalData.onCancel}
      onOk={removeUuidModalData.onOk}
    >
      <Text>{removeUuidModalData.description[0]}</Text>
      <Text style={{ marginTop: 4 }}>{removeUuidModalData.description[1]}</Text>
    </Modal>
  );

  const telegramUserPreview = (
    <>
      <TelegramUserPreview
        horizontal={true}
        telegramUser={getBasicDetailsFromUuidData?.data ?? storageData}
        showTelegramIcon={true}
        showInfoButton={true}
        style={{ marginBottom: 8, marginTop: 12 }}
        showingContinueWithoutTelegram={!getBasicDetailsFromUuidData}
      />
      {getBasicDetailsFromUuidData && (
        <Text style={{ textAlign: "center" }}>
          {t("external_account.validate.not_your_account")}{" "}
          <Link onClick={() => handleRemoveUuids()}>
            {t("external_account.validate.click_to_change")}
          </Link>{" "}
          {t("external_account.validate.change_suffix")}
        </Text>
      )}
    </>
  );

  return (
    <Wrapper $isMobile={isMobile && !inModal} style={{ maxWidth: 480 }}>
      <LoadingSpinnerOverlay
        isLoading={getBasicDetailsFromUuidData?.isFetching}
        spinnerMarginTop={150}
        hideContentWhileLoading={
          !getBasicDetailsFromUuidData?.getUserStatus.isSuccess
        }
        wrapStyle={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        spinnerSize="large"
      >
        <PrimaryTitle
          $isMobile={isMobile || inModal}
          style={{ marginBottom: 16 }}
        >
          {defaultTitle}
        </PrimaryTitle>
        <Security style={{ marginBottom: 20 }} />
        <Text style={{ textAlign: "center" }}>
          {t("external_account.validate.verification_prompt")}
        </Text>
        <Text style={{ textAlign: "center" }}>
          {t("external_account.validate.enter_code")}{" "}
          <Link href={securityCodeInputData.botUrl} target="_blank">
            {securityCodeInputData.botName}.
          </Link>
        </Text>
        <VerificationCode
          defaultCode={code}
          numOfDigits={NUM_OF_DIGITS_CODE}
          title={t("external_account.validate.security_code_label")}
          onSubmit={continueButtonData.onClick}
          additionalItems={telegramUserPreview}
          isLoading={continueButtonData.isLoading}
          actionButton={
            <SendCodeButton
              onSendAuthCodeClick={() => {
                onSendAuthCodeClick();
              }}
              showCodeSent={securityCodeInputData.showCodeSent}
              codeSentTooltipText={securityCodeInputData.codeSentTooltipText}
              showResendCode={securityCodeInputData.showResendCode}
            />
          }
        />
        {!getBasicDetailsFromUuidData && (
          <Link
            onClick={openRemoveUuidConfirmationModal}
            style={{ marginTop: 4 }}
          >
            {t("external_account.validate.continue_without")}
          </Link>
        )}
        {confirmRemoveUuidModal}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default ValidateConnectTelegram;

const NUM_OF_DIGITS_CODE = 6;
